<template>
  <v-dialog
    v-model="dialogState"
    persistent
    max-width="675px"
    role="alert"
    @keyup.escape="() => userAction(false)"
  >
    <v-card class="px-6 pb-2 pt-5" data-test="confirmDialog">
      <div class="d-flex flex-column flex-sm-row align-content-center mb-2">
        <div class="order-1 order-sm-0 flex-grow-1">
          <h1 class="confirm-title">
            <slot name="header">{{ title }}</slot>
          </h1>
        </div>
      </div>

      <div>
        <div
          :class="{
            'mb-10': $vuetify.breakpoint.smAndUp
          }"
        >
          <slot>
            <p v-show="!!message" v-html="message" />
            <v-text-field
              v-if="options.requireConfirmByName"
              v-model="confirmText"
              label="Write here"
              data-test="confirmText"
              outlined
            />
          </slot>
        </div>

        <v-row :dense="$vuetify.breakpoint.smAndUp" class="confirm-buttons">
          <v-col cols="12" sm="4" class="confirm-col">
            <v-btn
              rounded
              outlined
              class="confirm-button"
              data-test="confirmReject"
              @click="() => userAction(false)"
              ><slot name="cancel-text">{{ options.rejectText }}</slot></v-btn
            >
          </v-col>

          <v-col cols="12" sm="4" class="confirm-col">
            <v-btn
              rounded
              :color="options.confirmColor"
              :disabled="requireInputConfirmation"
              class="confirm-button"
              data-test="confirmAccept"
              @click="() => userAction(true)"
            >
              <slot name="submit-text">{{ options.confirmText }}</slot>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Vuetify Confirm Dialog component to use globally or locally
 *
 * For using globally, in any place in app:
 *  this.$root.$confirm('Delete', 'Are you sure?').then( async (confirm) => { if (confirm) { <your code in case answer was yes> } else { <your code in case answer was cancel> } } )
 *
 *
 * For local use, insert component where you want to use it:
 * <app-confirm ref="confirm"></app-confirm>
 *
 * Call it:
 * this.$refs.confirm.open('Delete', 'Are you sure?', { color: 'red' }).then((confirm) => {})
 * Or use await:
 * if (await this.$refs.confirm.open('Delete', 'Are you sure?', { color: 'red' })) {
 *   // yes
 * }
 * else {
 *   // cancel
 * }
 *
 * For global use, it has been already placed to main App component and can be accessible globally
 * via this.$root.$confirm
 *
 * <template>
 *   <v-app>
 *     ...
 *     <app-confirm ref="appConfirmDialog"/>
 *   </v-app>
 * </template>
 *
 * mounted() {
 *   this.$root.$confirm = this.$refs.appConfirmDialog.open
 * }
 */
import { defineComponent, ref, computed, reactive } from "vue";
import { useTranslation } from "@/plugins/i18next/i18next";

export default defineComponent({
  name: "AppConfirm",
  setup() {
    const { t } = useTranslation();

    const dialog = ref(false);
    const dialogState = computed({
      get() {
        return dialog.value;
      },
      set(val) {
        if (val === false) {
          window.onpopstate = () => {};
          confirmText.value = null;
          resolve.value(false);
        }

        dialog.value = val;
      }
    });

    const title = ref(null);
    const message = ref(null);

    const confirmText = ref(null);
    /**
     * When opened with requireConfirmByName value, have confirm button be disabled until
     * user types exact same value as requireConfirmByName is
     */
    const requireInputConfirmation = computed(() => {
      return (
        !!options.requireConfirmByName &&
        options.requireConfirmByName != confirmText.value
      );
    });

    const options = reactive({
      confirmColor: "accent",
      confirmText: null,
      rejectText: null,
      requireConfirmByName: null
    });

    const resolve = ref(null);
    //const reject = ref(null);

    /**
     *  Opens confirmation dialog.
     *  Mainly used trough $root.$confirm.open
     *  @param {string} titleProp
     *  @param {string} messageProp
     *  @param {object} confirmOptions { confirmText: string, rejectText: string, requireConfirmByName: string, confirmColor: string }
     *  @returns Promise object
     */
    function open(titleProp, messageProp, confirmOptions = {}) {
      dialogState.value = true;

      if (titleProp) title.value = titleProp;
      else title.value = t("common:LeavingPageWithoutSaving.title");

      if (messageProp) message.value = messageProp;
      else message.value = t("common:UnsavedData.disclaimer");

      options.confirmColor = confirmOptions.confirmColor || "accent";
      options.confirmText =
        confirmOptions.confirmText ||
        t("common:button.confirm", "Confirm", "Confirm button");
      options.rejectText =
        confirmOptions.rejectText ||
        t("common:button.cancel", "Cancel", "Cancel button");
      options.requireConfirmByName =
        confirmOptions.requireConfirmByName || null;

      return new Promise(promiseResolve => {
        resolve.value = promiseResolve;
      });
    }

    /**
     *  Simply opens confirmation box and sets resolve and reject
     *  Mainly used in local components when needing a lot more than just title and message (e.g PersonApplications when declining application)
     *  @returns Promise object
     */
    function blankSlateOpen() {
      options.confirmColor = "accent";
      options.confirmText = t(
        "common:button.confirm",
        "Confirm",
        "Confirm button"
      );
      options.rejectText = t("common:button.cancel", "Cancel", "Cancel button");

      dialogState.value = true;

      return new Promise(promiseResolve => {
        resolve.value = promiseResolve;
      });
    }

    function userAction(boolean) {
      resolve.value(boolean);
      dialogState.value = false;
    }

    return {
      dialogState,
      title,
      message,
      confirmText,
      requireInputConfirmation,
      options,
      userAction,

      open,
      blankSlateOpen
    };
  }
});
</script>
<style lang="scss">
.confirm-title {
  font-size: 24px;
  margin-bottom: 8px;
  @media (max-width: 576px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.confirm-buttons {
  justify-content: center;

  @media (min-width: 577px) {
    justify-content: end;
    .confirm-col {
      text-align: end;
    }
  }

  .confirm-col {
    text-align: center;
  }

  .confirm-button {
    min-height: 40px;
    width: 295px;
    text-align: center;

    @media (min-width: 577px) {
      width: 100%;
      max-width: 185px;
      text-align: end;
    }
  }
}
</style>
